import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { toast } from 'react-toastify';
import { camelCaseToKebabCase } from '../utils/general';

const API_ROOT = process.env.REACT_APP_API_ROOT || '/api';

export const api = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({ baseUrl: API_ROOT }),
  // TODO: update tags
  // tagTypes: ['Examinee', 'Exam_Records', 'Testing_Cycles', 'Raters', 'Exams'],
  endpoints: build => ({
    getAreaOptionsData: build.query({
      query: () => '/filters/areas',
    }),
    getBoundaryData: build.query({
      query: ({ selectedCounty }) => {
        return `/map/area?county=${selectedCounty}`;
      },
    }),
    getStructureData: build.query({
      query: ({ selectedCounty }) => {
        return `/map/structures?county=${selectedCounty}`;
      },
    }),
    getFloodInfrastructureData: build.query({
      query: ({ selectedCounty }) => {
        return `map/flood-infrastructure?county=${selectedCounty}`;
      },
    }),
    getBleDepData: build.query({
      query: ({ selectedCounty }) => {
        return `map/ble-dep?county=${selectedCounty}`;
      },
    }),
    getCat1surgeData: build.query({
      query: ({ selectedCounty }) => {
        return `map/cat-1-surge?county=${selectedCounty}`;
      },
    }),
    getCat2surgeData: build.query({
      query: ({ selectedCounty }) => {
        return `map/cat-2-surge?county=${selectedCounty}`;
      },
    }),
    getCat3surgeData: build.query({
      query: ({ selectedCounty }) => {
        return `map/cat-3-surge?county=${selectedCounty}`;
      },
    }),
    getCat4surgeData: build.query({
      query: ({ selectedCounty }) => {
        return `map/cat-4-surge?county=${selectedCounty}`;
      },
    }),
    getCat5surgeData: build.query({
      query: ({ selectedCounty }) => {
        return `map/cat-5-surge?county=${selectedCounty} `;
      },
    }),
    getHUC08Data: build.query({
      query: ({ selectedCounty }) => {
        return `map/huc08?county=${selectedCounty}`;
      },
    }),
    getHUC10Data: build.query({
      query: ({ selectedCounty }) => {
        return `map/huc10?county=${selectedCounty}`;
      },
    }),
    getHUC12Data: build.query({
      query: ({ selectedCounty }) => {
        return `map/huc12?county=${selectedCounty}`;
      },
    }),
    getWaterbodiesData: build.query({
      query: ({ selectedCounty }) => {
        return `map/waterbodies?county=${selectedCounty}`;
      },
    }),
    getHurricaneTracksData: build.query({
      query: ({ selectedCounty }) => {
        return `map/hurricane-tracks?county=${selectedCounty}`;
      },
    }),
    getFlowlinesData: build.query({
      query: ({ selectedCounty }) => {
        return `map/flowlines?county=${selectedCounty}`;
      },
    }),
    getSurfaceWaterGauges: build.query({
      query: ({ selectedCounty }) => {
        return `map/surface-water-gauges?county=${selectedCounty}`;
      },
    }),
    getRainGauge: build.query({
      query: ({ selectedCounty }) => {
        return `map/rain-gauges?county=${selectedCounty}`;
      },
    }),
    // ADDRESS VALIDATION:
    validateAddress: build.query({
      query: ({ lat, lon }) => ({
        url: '/map/validate-address',
        params: { lat, lon },
      }),
    }),
  }),
});

export const downloadGeoJson = async (datasetId, filterInfo) => {
  try {
    const geographyParams = `county=${filterInfo.selectedCounty}`;
    const datasetParams = camelCaseToKebabCase(datasetId);

    const fileFetch = await fetch(
      `${API_ROOT}/downloads/${datasetParams}/geojson?${geographyParams}`,
    );
    const contentDisposition = fileFetch.headers.get('Content-Disposition');
    let filename = `${filterInfo.selectedCounty}.geojson`; // fallback filename

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    const stream = await fileFetch.blob();
    const blob = new Blob([stream], { type: 'application/geo+json' });
    const dataUrl = window.URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = dataUrl;
    downloadLink.download = filename;
    downloadLink.click();
    document.body.appendChild(downloadLink);
    document.body.removeChild(downloadLink);
    window.URL.revokeObjectURL(dataUrl);
  } catch (error) {
    toast.error('Oops! We are unable to download this dataset, please try again later.');
    console.error(error);
  }
};

export const downloadTiff = async (datasetId, filterInfo) => {
  try {
    const geographyParams = `county=${filterInfo.selectedCounty}`;
    const tiffEndpoints = {
      burnProbability: 'burn-probability-tiff',
      rainfall: 'rainfall-tiff',
      minTemp: 'minimum-temp-tiff',
      maxTemp: 'maximum-temp-tiff',
      bleDep100: 'ble-dep-100-tiff',
      bleDep500: 'ble-dep-500-tiff',
      wse100: 'wse-100-tiff',
      wse500: 'wse-500-tiff',
      cat1surge: 'cat-1-surge-tiff',
      cat2surge: 'cat-2-surge-tiff',
      cat3surge: 'cat-3-surge-tiff',
      cat4surge: 'cat-4-surge-tiff',
      cat5surge: 'cat-5-surge-tiff',
      combinedSurge: 'combined-storm-surge-tiff',
    };

    const endpoint = tiffEndpoints[datasetId];
    if (!endpoint) {
      toast.error(`No TIFF download endpoint configured for: ${datasetId}`);
      console.error('No TIFF download endpoint configured for:', datasetId);
      return;
    }

    const downloadUrl = `${API_ROOT}/downloads/${endpoint}?${geographyParams}`;
    const response = await fetch(downloadUrl);
    if (!response.ok) {
      toast.error('Oops! We are unable to download this dataset, please try again later.');
      throw new Error(`Download failed: ${response.status} ${response.statusText}`);
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filterInfo.areaName}-${datasetId}.tif`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    toast.error('Oops! We are unable to download this dataset, please try again later.');
    console.error(error);
  }
};

export const {
  useGetAreaOptionsDataQuery,
  useLazyGetBoundaryDataQuery,
  useLazyGetHUC08DataQuery,
  useLazyGetHUC10DataQuery,
  useLazyGetHUC12DataQuery,
  useLazyGetFloodInfrastructureDataQuery,
  useLazyGetStructureDataQuery,
  useLazyGetFlowlinesDataQuery,
  useLazyGetCat1surgeDataQuery,
  useLazyGetCat2surgeDataQuery,
  useLazyGetCat3surgeDataQuery,
  useLazyGetCat4surgeDataQuery,
  useLazyGetCat5surgeDataQuery,
  useLazyGetHurricaneTracksDataQuery,
  useLazyGetBleDepDataQuery,
  useLazyGetWaterbodiesDataQuery,
  useLazyValidateAddressQuery,
  useLazyGetSurfaceWaterGaugesQuery,
  useLazyGetRainGaugeQuery,
} = api;
